import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { LuEye, LuEyeOff, LuLoaderCircle, LuCircleAlert, LuCircleCheck } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import BotaoGoogle from '../components/BotaoGoogle';
import { useCelular } from '../hooks/useCelular';
import { destinoAposEntrar } from '../config/boasVindas';

/**
 * /login — Direção B do desenho de 2026-09-18
 * (`docs/designsystem/propostas/login.html`).
 *
 * O formulário mora num bloco com filete, centrado na tela; a faixa de cima
 * saiu e o "criar conta" desceu para o rodapé do bloco, a pedido dele.
 *
 * O que motivou: na casca anterior esta tela preenchia 43% da área útil e
 * deixava 384px de vão embaixo — a casca tinha sido desenhada com o cadastro à
 * vista, que tem o dobro de conteúdo.
 */
const Login = () => {
  const celular = useCelular();
  const apiUrl = import.meta.env.VITE_API_URL;
  const endpointLogin = import.meta.env.VITE_ENDPOINT_LOGIN;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [lembrar, setLembrar] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [erroGoogle, setErroGoogle] = useState<string | null>(null);

  const campoSenha = useRef<HTMLInputElement>(null);

  /* Campos em `readOnly` até a pessoa encostar na página.
   *
   * Pedido dele em 18/09, depois de recarregar e ver e-mail e senha de OUTRO
   * sistema preenchidos. Só os atributos não bastam: o Chrome e o Edge ignoram
   * `autocomplete="off"` em formulário que eles reconhecem como login, e o
   * gerenciador guarda credencial por ORIGEM — `localhost:5174` é a mesma
   * origem para todo projeto que roda nessa porta. Campo `readOnly`, o
   * navegador não preenche.
   *
   * A trava cai no primeiro gesto REAL do usuário (ponteiro ou tecla), não no
   * `autoFocus`: se caísse no foco programático, o navegador preencheria ali
   * mesmo. `keydown` roda antes do caractere entrar, então nada se perde ao
   * começar a digitar. */
  const [travado, setTravado] = useState(true);

  useEffect(() => {
    if (!travado) return;
    const destravar = () => setTravado(false);
    document.addEventListener('pointerdown', destravar, { once: true });
    document.addEventListener('keydown', destravar, { once: true });
    return () => {
      document.removeEventListener('pointerdown', destravar);
      document.removeEventListener('keydown', destravar);
    };
  }, [travado]);

  /* Quem já tem sessão não vê o login. Até 16/10/2026 esta tela nunca conferia: o "/" e o
     "/login" pediam e-mail e senha mesmo com a sessão de 7 dias valendo — no computador, quem
     voltava à tarde pelo favorito caía aqui; no celular, o app instalado abre no "/" e pediria
     login a cada toque no ícone. O access token vence em 1h, então o 401 do Me é o normal de
     quem voltou depois: renova pelo refresh token (7 dias) e tenta de novo. Só fica no login
     quem não tem sessão nenhuma. Fetch puro, e não o apiFetch, para um 401 aqui não recarregar
     a página.

     Enquanto confere, o formulário não aparece — pedido dele em 16/10, depois de ver o login
     piscar antes de ir para a Home. A pista é o `user` do localStorage, que o logout e o 401
     sem renovação apagam: quem não tem a pista vê o formulário na hora, sem tela em branco.
     Os 4s são a trava para uma API que não responde não deixar a pessoa sem formulário. */
  const [conferindo, setConferindo] = useState(() => localStorage.getItem('user') !== null);

  useEffect(() => {
    let vivo = true;
    const desistir = setTimeout(() => setConferindo(false), 4000);
    const me = () => fetch(`${apiUrl}/api/Auth/Me`, { credentials: 'include' });
    (async () => {
      try {
        let resposta = await me();
        if (resposta.status === 401) {
          const renovou = await fetch(`${apiUrl}/api/Auth/refresh-token`, { method: 'POST', credentials: 'include' });
          if (!renovou.ok) return false;
          resposta = await me();
        }
        if (!vivo || !resposta.ok) return false;
        localStorage.setItem('user', JSON.stringify(await resposta.json()));
        navigate(destinoAposEntrar(), { replace: true });
        return true;
      } catch { return false; /* sem rede: fica no login, como antes */ }
      finally { clearTimeout(desistir); }
    })().then(entrou => { if (vivo && !entrou) setConferindo(false); });
    return () => { vivo = false; clearTimeout(desistir); };
  }, [apiUrl, navigate]);

  /* Quem chega de um link de confirmação ou de uma troca de senha sem sessão. */
  const emailConfirmado = searchParams.get('confirmado') === '1';
  const senhaRedefinida = searchParams.get('senha') === '1';

  useEffect(() => {
    if (localStorage.getItem('rememberMe') === 'true') {
      setLembrar(true);
      setEmail(localStorage.getItem('rememberedEmail') || '');
    }
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (carregando) return;

    /* Depois de um erro a senha é limpa e recebe o foco; um Enter em seguida
       mandava a senha vazia para a API, que respondia como erro do servidor. */
    if (!email.trim() || !senha) {
      setErro('Informe o e-mail e a senha.');
      if (!email.trim()) document.getElementById('login-email')?.focus();
      else campoSenha.current?.focus();
      return;
    }

    setCarregando(true);
    setErro(null);

    try {
      const resposta = await fetch(`${apiUrl}${endpointLogin}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: senha }),
      });

      if (!resposta.ok) {
        /* Convidado que tentou entrar antes de criar a senha pelo link: a API diz
           o motivo, e repetir "não conferem" aqui só faria a pessoa tentar de novo. */
        const corpo = await resposta.json().catch(() => ({}));
        if (corpo?.convitePendente) {
          setErro(corpo.message || 'Você ainda não aceitou o convite. Abra o e-mail que enviamos e crie a sua senha.');
          setCarregando(false);
          return;
        }
        throw new Error('Falha na autenticação');
      }

      const me = await fetch(`${apiUrl}/api/Auth/Me`, { credentials: 'include' });
      if (!me.ok) throw new Error('Falha na autenticação de sessão');

      localStorage.setItem('user', JSON.stringify(await me.json()));

      if (lembrar) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberedEmail');
      }

      /* A Home é a porta de entrada desde 2026-09-11 (decisão do cliente): ela
         não exige permissão nenhuma, então serve para qualquer perfil — inclusive
         os que não alcançam /monitoramento. Desde 29/10/2026 vale também no
         celular, que até então caía na Pipeline. E quem criou a conta e ainda não
         passou pelo guia de boas-vindas cai nele antes (a API diz, em `boasVindas`).
         O destino mora em `config/boasVindas.ts`, para os caminhos de entrada
         (login, cadastro, Google, convite) não repetirem a regra. */
      navigate(destinoAposEntrar());
    } catch (err) {
      console.error(err);
      setErro('E-mail ou senha não conferem.');
      /* O foco volta para a senha, que é o campo que quase sempre errou —
         antes ele ficava no alerta e a pessoa tinha que reclicar. */
      setSenha('');
      campoSenha.current?.focus();
    } finally {
      setCarregando(false);
    }
  };

  /* Nada na tela, nem a casca: ela força o tema claro, e quem usa o escuro levaria um clarão
     antes da Home. */
  if (conferindo) return null;

  return (
    <AuthShell>
      <h1 className="au-h1">Acesse sua conta</h1>
      <p className="au-sub">Entre para continuar de onde parou.</p>

      <BotaoGoogle onErro={setErroGoogle} />
      {/* Aviso próprio, junto do botão: o `erro` do formulário pinta a senha de vermelho. */}
      {erroGoogle && (
        <div className="au-alert is-danger" role="alert">
          <LuCircleAlert size={16} />
          <span>{erroGoogle}</span>
        </div>
      )}
      <div className="au-ou" aria-hidden="true">ou com e-mail</div>

      <form onSubmit={handleSubmit} noValidate>
        {emailConfirmado && (
          <div className="au-alert" role="status">
            <LuCircleCheck size={16} />
            <span>E-mail confirmado. Entre para continuar.</span>
          </div>
        )}

        {senhaRedefinida && (
          <div className="au-alert" role="status">
            <LuCircleCheck size={16} />
            <span>Senha alterada. Entre com a nova.</span>
          </div>
        )}

        {erro && (
          <div className="au-alert is-danger" role="alert">
            <LuCircleAlert size={16} />
            <span>{erro}</span>
          </div>
        )}

        <div className="au-group">
          <label className="au-label" htmlFor="login-email">E-mail</label>
          <div className="au-input-wrap">
            <input
              id="login-email"
              className="au-input"
              type="email"
              inputMode="email"
              placeholder="voce@suaempresa.com.br"
              value={email}
              /* Preenchimento automático DESLIGADO por pedido dele em 18/09: o
                 navegador vinha com a credencial de outro sistema (localhost é a
                 mesma origem para todo projeto que roda na 5174).
                 `off` sozinho o Chrome ignora em formulário de login; o que ele
                 respeita é um token que não descreve credencial — daí o par
                 "email" → "off" aqui e "current-password" → "new-password" no
                 campo de senha, que é o que desarma o gerenciador. */
              readOnly={travado}
              autoComplete="off"
              autoFocus
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="au-group">
          <div className="au-label-row">
            <label className="au-label" htmlFor="login-password">Senha</label>
            <Link to="/esqueci-senha" state={{ email }}>Esqueci minha senha</Link>
          </div>
          <div className="au-input-wrap has-toggle">
            <input
              id="login-password"
              ref={campoSenha}
              className={`au-input${erro ? ' is-error' : ''}`}
              type={mostrarSenha ? 'text' : 'password'}
              placeholder="••••••••"
              value={senha}
              readOnly={travado}
              autoComplete="new-password"
              onChange={e => setSenha(e.target.value)}
              required
            />
            <button
              type="button"
              className="au-toggle"
              onClick={() => setMostrarSenha(v => !v)}
              aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {mostrarSenha ? <LuEyeOff size={16} /> : <LuEye size={16} />}
            </button>
          </div>
        </div>

        <div className="au-options">
          <label className="au-check">
            <input type="checkbox" checked={lembrar} onChange={e => setLembrar(e.target.checked)} />
            {celular ? 'Manter conectado neste aparelho' : 'Manter conectado neste computador'}
          </label>
        </div>

        <button type="submit" className={`au-btn${carregando ? ' is-loading' : ''}`}>
          {carregando ? (
            <><LuLoaderCircle size={16} className="au-spin" /> Entrando…</>
          ) : (
            'Entrar'
          )}
        </button>
      </form>

      <p className="au-bloco-foot">
        Não tem conta? <Link to="/cadastro">Criar conta grátis</Link>
      </p>
    </AuthShell>
  );
};

export default Login;
